import { IDirectoryNode } from 'app/services/documents';
import { FolderSolidIcon } from 'assets/icons';
import DebouncedSearch from 'Components/DebouncedSearch';
import { FilterGroup, Filters, FilterSection } from 'Components/Filters';
import { DateFilter } from 'Components/Filters/DateFilter';
import { TriStateFilter } from 'Components/Filters/TriStateFilter';
import { ClientBusinessTaskTaxReturnSelect } from 'Components/Selectors/ClientBusinessTaskTaxReturnSelect';
import { useAuth } from 'utils/AuthProvider';
import { omit, useDocumentsContext } from '.';

export type DocumentsFiltersType = {
  id?: number;
  from_date?: string;
  to_date?: string;
  client_id?: number;
  business_id?: number;
  task_id?: number;
  project_id?: number;
  mine?: boolean;
  object_type?: 'document' | 'folder'; // small case
  search?: string;
  is_trashed?: boolean;
};
export type DocumentsFiltersProps = {
  value: DocumentsFiltersType;
  directory?: IDirectoryNode;
  onChange: (f: DocumentsFiltersType) => void;
};

export default function DocumentsFilters({
  value,
  onChange,
  directory,
}: DocumentsFiltersProps) {
  const { isClient } = useAuth();
  const { lockClient, lockTask, lockProject } = useDocumentsContext();

  let locked: (keyof DocumentsFiltersType)[] = [
    'id',
    'object_type',
    'is_trashed',
  ];
  if (lockClient) {
    locked.push('client_id');
  }
  if (lockTask) {
    locked.push('task_id');
  }
  if (lockProject) {
    locked.push('project_id');
  }

  const popupFilters = omit('search', value);
  return (
    <div className="flex gap-2 items-center">
      <DebouncedSearch
        value={value.search}
        onChange={(search) =>
          onChange({
            ...value,
            search,
          })
        }
      />
      <Filters
        isLoading={false}
        preExpanded={[]}
        value={{ ...(popupFilters as DocumentsFiltersType) }}
        onConfirm={(c) => onChange({ ...c, search: value.search })}
        locked={locked}
      >
        {({
          updateField,
          state: {
            id,
            business_id,
            client_id,
            project_id,
            task_id,
            from_date,
            to_date,
            mine,
          },
          clear,
        }) => (
          <>
            {id && (
              <FilterSection>
                <div className="flex gap-2 items-center text-gray-dark font-semibold my-2">
                  <FolderSolidIcon /> {directory && directory.title}
                </div>
              </FilterSection>
            )}
            <TriStateFilter
              value={mine}
              uuid="Documents"
              label="Documents"
              buttonOne={isClient ? 'My Documents' : 'AOAs Documents'}
              buttonTwo={isClient ? 'Shared With Me' : 'Clients Documents'}
              onChange={(newValue) => updateField('mine', newValue)}
            />

            <FilterSection>
              <div className="w-full">
                <ClientBusinessTaskTaxReturnSelect
                  businessId={business_id}
                  clientId={lockClient ? lockClient.id : client_id}
                  lockClient={lockClient}
                  lockProject={lockProject}
                  lockTask={lockTask}
                  taskId={lockTask ? lockTask.id : task_id}
                  projectId={lockProject ? lockProject.id : project_id}
                  onChange={({ clientId, businessId, taskId, projectId }) => {
                    updateField('client_id', clientId);
                    updateField('business_id', businessId);
                    updateField('task_id', taskId);
                    updateField('project_id', projectId);
                  }}
                />
              </div>
            </FilterSection>

            <FilterGroup
              label="Date"
              onClear={() => {
                updateField('from_date', undefined);
                updateField('to_date', undefined);
              }}
              isDirty={from_date || to_date}
              uuid="created_at"
            >
              <DateFilter
                label="From"
                maxDate={to_date ? new Date(to_date) : new Date()}
                value={from_date}
                onChangeStringDate={(from) => updateField('from_date', from)}
              />

              <DateFilter
                label="To"
                minDate={from_date}
                maxDate={new Date()}
                value={to_date}
                hourOffset="max"
                onChangeStringDate={(to) => updateField('to_date', to)}
              />
            </FilterGroup>
          </>
        )}
      </Filters>
    </div>
  );
}
